import type { ApplyUpdatePhase, VersionInfo } from './types';

/**
 * Pure version-comparison logic, extracted from the hook so it is unit-testable
 * in isolation. No React, no DOM — safe to import anywhere.
 */

const MAX_FIELD_LEN = 64;
// Anchored: a semver-ish core with an optional bounded pre-release/build suffix.
// An unanchored regex would let arbitrary trailing text ride into the UI label.
const SEMVER_ISH = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.+-]{1,32})?$/;
// Git short/full sha, or the explicit "unknown" fallback gen-version.mjs bakes
// when git is unavailable at build time. Anything else fails closed.
const SHA_RE = /^(?:[0-9a-f]{7,40}|unknown)$/;
/** Tolerate a minute of client/CDN clock skew when comparing a support floor. */
const CLOCK_SKEW_MS = 60 * 1000;

export function majorOf(version: string): number {
  const n = parseInt(String(version).split('.')[0], 10);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Shape + value sanity guard. version.json is same-origin and unsigned, but it
 * drives an auto-reload + forced modal, so we bound field sizes and require a
 * semver-ish `version` before trusting it. Fails closed (→ "no update").
 */
export function isValidVersionInfo(v: unknown): v is VersionInfo {
  if (!v || typeof v !== 'object') return false;
  const c = v as Record<string, unknown>;
  const strOk = (x: unknown, max = MAX_FIELD_LEN): x is string =>
    typeof x === 'string' && x.length > 0 && x.length <= max;
  if (!strOk(c.sha) || !strOk(c.builtAt) || !strOk(c.version)) return false;
  if (!SHA_RE.test(c.sha)) return false;
  if (!SEMVER_ISH.test(c.version)) return false;
  if (Number.isNaN(Date.parse(c.builtAt))) return false;
  if (
    c.minSupportedBuiltAt !== null &&
    c.minSupportedBuiltAt !== undefined &&
    !strOk(c.minSupportedBuiltAt)
  ) {
    return false;
  }
  return true;
}

/**
 * A strictly newer build: different sha AND a later builtAt. The builtAt guard
 * stops a stale CDN edge (older version.json) from ever showing a false update.
 */
export function isNewer(latest: VersionInfo, current: VersionInfo): boolean {
  if (latest.sha === current.sha) return false;
  const lt = Date.parse(latest.builtAt);
  const ct = Date.parse(current.builtAt);
  if (Number.isNaN(lt) || Number.isNaN(ct)) return latest.sha !== current.sha;
  return lt > ct;
}

/**
 * Whether the drift is large enough to FORCE (vs. soft-prompt): the running
 * build predates a published support floor. A floor dated in the future (clock
 * skew, typo'd env, not-yet-live build) is ignored so a bad value can't lock
 * out the entire user base.
 *
 * NOTE: forcing is driven SOLELY by `minSupportedBuiltAt`, the deliberate
 * operator-controlled floor. It intentionally does NOT force on a version-number
 * increase: the build `version` is now date-based (`YYYY.MM.DD`, see
 * scripts/app-version/gen-version.mjs), so the leading segment is the calendar
 * year. A `majorOf(latest) > majorOf(current)` rule would force the ENTIRE user
 * base every Jan 1 (year 2027 > 2026) — a calendar artifact, not real drift.
 * `majorOf` is retained as a pure utility but is no longer a forcing signal.
 */
export function isForced(latest: VersionInfo, current: VersionInfo): boolean {
  if (latest.minSupportedBuiltAt) {
    const floor = Date.parse(latest.minSupportedBuiltAt);
    const ct = Date.parse(current.builtAt);
    if (
      !Number.isNaN(floor) &&
      !Number.isNaN(ct) &&
      floor <= Date.now() + CLOCK_SKEW_MS &&
      ct < floor
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Human-readable build label for update UI. The version alone is NOT a build
 * identity — the version is date-based (`YYYY.MM.DD`), so two builds shipped on
 * the same day share a version and "v2026.08.27 → v2026.08.27" is meaningless.
 * The sha (what update detection actually keys on) makes old ≠ new visible.
 */
export function buildLabel(info: VersionInfo): string {
  if (info.sha === 'unknown') return `v${info.version}`;
  // Bound the display: validation allows a full 40-char sha, but the compact
  // banner/modal chrome only needs enough to tell two builds apart.
  return `v${info.version} (${info.sha.slice(0, 12)})`;
}

/**
 * True while an applyUpdate run is in flight and the trigger control should be
 * disabled. `failed` is terminal-for-the-attempt: the control re-arms so the
 * user can retry. Shared by the forced modal and the soft banner so the two
 * surfaces cannot drift on what "in flight" means.
 */
export function isUpdateInFlight(
  phase: ApplyUpdatePhase | null,
): phase is Exclude<ApplyUpdatePhase, 'failed'> {
  return phase !== null && phase !== 'failed';
}

/**
 * Control label per update phase — narrates a possibly-minutes-long install
 * instead of pretending a spinner means seconds. Exhaustive over
 * ApplyUpdatePhase (no default) so adding a phase is a compile error at every
 * consumer, not a silent generic label.
 */
export function phaseLabel(phase: ApplyUpdatePhase): string {
  switch (phase) {
    case 'checking':
      return 'Checking...';
    case 'downloading':
      return 'Downloading update...';
    case 'activating':
      return 'Almost done...';
    case 'reloading':
      return 'Restarting...';
    case 'failed':
      return 'Try again';
  }
}
